/**
 * Persistent chat history for the Realtime-Voice guide.
 *
 * Simple localStorage wrapper. Stores:
 *   - the last N transcript bubbles (both speakers, non-partial only)
 *   - the last focused knowledge id (so a resumed session lands back
 *     on the species view the user was looking at)
 *   - a timestamp for optional TTL enforcement
 *
 * Consumers:
 *   - VoiceAgentController rehydrates its internal `_transcript` from
 *     `loadHistory()` at construction time.
 *   - After each transcript mutation the controller calls `saveHistory()`
 *     (debounced) so the browser stays in sync.
 *   - Session priming: when the DataChannel opens, the controller emits
 *     `conversation.item.create` for the last `PRIME_BUBBLE_CAP` bubbles
 *     BEFORE the greeting `response.create`, so the Realtime model reads
 *     "here is what you and the visitor already discussed" and continues
 *     naturally instead of starting from zero.
 *
 * Anonymous by design — no server round-trip. When Alex adds the
 * Google-Login-merge later (Phase 2), this same localStorage cache
 * becomes the source that gets synced up to guide-api on first login.
 */

import type { TranscriptEntry } from './VoiceAgentController'

const TRANSCRIPT_KEY = 'tschauko-history-v1'
const MAX_STORED_BUBBLES = 20
const MAX_STORED_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
/** How many past bubbles to prime the Realtime session with. Kept small
 *  so token-priming stays affordable (each bubble = a few tokens input).
 *  Alternating user/assistant makes 6 → ~3 exchanges of context. */
export const PRIME_BUBBLE_CAP = 6

export interface StoredHistory {
  version: 1
  savedAt: number
  transcript: TranscriptEntry[]
  focusedKnowledgeId: number | null
}

/** Read the persisted history. Returns null on empty / corrupt / expired. */
export function loadHistory(): StoredHistory | null {
  try {
    const raw = localStorage.getItem(TRANSCRIPT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredHistory>
    if (parsed.version !== 1 || !Array.isArray(parsed.transcript)) return null
    // TTL: forget history older than 30 days — old sessions would just
    // confuse the model when re-primed ("continue the conversation from
    // a month ago") and take up localStorage quota for no gain.
    if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > MAX_STORED_AGE_MS) {
      localStorage.removeItem(TRANSCRIPT_KEY)
      return null
    }
    return {
      version: 1,
      savedAt: parsed.savedAt ?? Date.now(),
      transcript: parsed.transcript.filter(isValidBubble).slice(-MAX_STORED_BUBBLES),
      focusedKnowledgeId: typeof parsed.focusedKnowledgeId === 'number' ? parsed.focusedKnowledgeId : null,
    }
  } catch {
    return null
  }
}

/** Persist the current transcript + focus. Silently no-ops on quota
 *  errors so a full localStorage never breaks the app. */
export function saveHistory(
  transcript: TranscriptEntry[],
  focusedKnowledgeId: number | null,
): void {
  try {
    const finished = transcript.filter(isValidBubble).slice(-MAX_STORED_BUBBLES)
    const payload: StoredHistory = {
      version: 1,
      savedAt: Date.now(),
      transcript: finished,
      focusedKnowledgeId,
    }
    localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(payload))
  } catch {
    /* quota exceeded / private-mode — ignore */
  }
}

/** Explicit reset. Bound to a UI action later ("Verlauf löschen"). */
export function clearHistory(): void {
  try { localStorage.removeItem(TRANSCRIPT_KEY) } catch { /* noop */ }
}

/** The last N bubbles suitable for session priming — non-partial,
 *  non-empty, alternating speakers as recorded. Older entries first
 *  (ready to be replayed in order). */
export function bubblesForPriming(transcript: TranscriptEntry[]): TranscriptEntry[] {
  return transcript.filter(isValidBubble).slice(-PRIME_BUBBLE_CAP)
}

function isValidBubble(b: unknown): b is TranscriptEntry {
  if (!b || typeof b !== 'object') return false
  const e = b as TranscriptEntry
  return (
    (e.speaker === 'user' || e.speaker === 'bot') &&
    typeof e.text === 'string' &&
    e.text.length > 0 &&
    !e.partial
  )
}
